import React, { useState } from "react";
import { Box, Button, TextField, Autocomplete, CircularProgress } from "@mui/material";
import { BookmarkAdd } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import emailContactService from "../../services/emailContactService";

// Recipient field shared by every "send an email to the forwarder" dialog —
// an editable, freeSolo autocomplete over the admin's saved email contacts,
// with a one-click "save this address" action so it doesn't have to be
// retyped next time.
const RecipientPicker = ({ value, onChange, contacts, onSaved }) => {
    const [saving, setSaving] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
    const alreadySaved = contacts.some((c) => c.email === value.trim().toLowerCase());

    const handleSave = async () => {
        setSaving(true);
        try {
            await emailContactService.create(undefined, value.trim());
            enqueueSnackbar("Email saved to list", { variant: "success" });
            onSaved();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save email", {
                variant: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <Autocomplete
                freeSolo
                fullWidth
                options={contacts}
                getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label ? `${opt.label} <${opt.email}>` : opt.email)}
                inputValue={value}
                onInputChange={(_, newValue) => onChange(newValue)}
                onChange={(_, newValue) => {
                    if (!newValue) return onChange("");
                    onChange(typeof newValue === "string" ? newValue : newValue.email);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        autoFocus
                        label="Recipient Email"
                        placeholder="freightforwarder@example.com"
                    />
                )}
            />
            <Button
                variant="outlined"
                startIcon={saving ? <CircularProgress size={16} /> : <BookmarkAdd />}
                onClick={handleSave}
                disabled={!isValidEmail || alreadySaved || saving}
                sx={{ mt: 0.25, whiteSpace: "nowrap", flexShrink: 0 }}
            >
                {alreadySaved ? "Saved" : "Save"}
            </Button>
        </Box>
    );
};

export default RecipientPicker;
